/**
 * Which app shell is this page running inside, and how old is it?
 *
 * The web deploys in minutes. A shell update waits days for store review, then weeks while
 * people get round to updating — and some never do. So every change lives for a while in a world
 * where the current web runs inside an older shell, and the two cannot be reasoned about as one
 * thing. The shell injects `__isNativeApp`, `__appPlatform` and `__appVersion`
 * (mobile/src/components/SharedWebView.js); this is the one place that reads them.
 *
 * ── Ask what the shell can do before you ask how old it is ───────────────────────────────────
 *
 * When the question is "can this shell do X", ask the shell: `__nativeShareSupported`,
 * `__nativeCameraSupported`, `__nativeDictationSupported` and friends are set by the shell that
 * actually has the feature. Prefer them. A version number cannot answer that question honestly,
 * because the two stores review independently: 1.9.9 can be live on iOS while Android is still
 * in the queue a week later, so `atLeast('1.9.9')` is true and false at the same time depending
 * on which phone is asking.
 *
 * ── Use the version when there is no flag to ask ─────────────────────────────────────────────
 *
 * Bugs do not advertise themselves. A shell that drops the link out of a share sheet has no
 * `__shareDropsUrl` flag and never will; its version is the only thing that identifies it. Same
 * for anything you want to roll out gradually, and for simply measuring how the installed base
 * splits. That is what this is for.
 */

/** Numeric compare of two dotted versions. Missing components count as 0, so '1.9' === '1.9.0'. */
export function compareVersions(a, b) {
  const parse = (v) => String(v || '').split('.').map((n) => {
    const x = parseInt(n, 10);
    return Number.isFinite(x) ? x : 0;
  });
  const pa = parse(a);
  const pb = parse(b);
  const len = Math.max(pa.length, pb.length, 3);
  for (let i = 0; i < len; i++) {
    if ((pa[i] || 0) < (pb[i] || 0)) return -1;
    if ((pa[i] || 0) > (pb[i] || 0)) return 1;
  }
  return 0;
}

export function isNativeApp() {
  return typeof window !== 'undefined' && !!window.__isNativeApp;
}

/** 'ios' | 'android' | null. Falls back to the UA only inside the app, never in a browser. */
export function appPlatform() {
  if (!isNativeApp()) return null;
  if (window.__appPlatform === 'ios' || window.__appPlatform === 'android') return window.__appPlatform;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'ios' : 'android';
}

/**
 * The shell's version, or null when it cannot be known.
 *
 * Two ways it is unknown, and both mean "older than anything we would gate on": a shell built
 * before this global existed leaves it undefined, and the shell itself falls back to '0.0.0'
 * when Constants.expoConfig has no version.
 */
export function appVersion() {
  if (!isNativeApp()) return null;
  const v = window.__appVersion;
  if (typeof v !== 'string' || !v || v === '0.0.0') return null;
  return v;
}

/**
 * Running in the app, on a shell at or above `version`.
 *
 * False in a browser and false when the version is unknown — so this is the safe side to put a
 * new capability on: anything that needs a shell you cannot confirm simply does not happen.
 */
export function atLeast(version) {
  const v = appVersion();
  return v !== null && compareVersions(v, version) >= 0;
}

/**
 * Running in the app, on a shell below `version` (or one too old to say).
 *
 * Note this is deliberately NOT the negation of atLeast(): in a browser both are false. A
 * browser is not an old app, and a workaround for an old shell has no business running in one.
 * Use this for "work around a bug that version fixed", and atLeast() for "use something that
 * version added".
 */
export function below(version) {
  if (!isNativeApp()) return false;
  const v = appVersion();
  return v === null || compareVersions(v, version) < 0;
}
